import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  FaCloudUploadAlt, 
  FaSyncAlt, 
  FaShieldAlt, 
  FaHistory, 
  FaArrowLeft,
  FaArrowRight
} from "react-icons/fa";
import './AutoBackupSoftware.css';

// Laptop Slideshow Component for displaying the 5 Autobackup images inside a premium mockup
const LaptopSlideshow = ({ images, altText, autoPlayInterval = 3000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (isHovered) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }, autoPlayInterval);
    return () => clearInterval(interval);
  }, [images.length, autoPlayInterval, isHovered]);

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (index, e) => {
    e.stopPropagation();
    setCurrentIndex(index);
  };

  return (
    <div 
      className="laptop-mockup"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="laptop-screen">
        <div className="screen-reflection" />
        <div 
          className="screen-track" 
          style={{ 
            transform: `translate3d(-${currentIndex * 100}%, 0, 0)`
          }}
        >
          {images.map((img, idx) => (
            <div className="screen-slide" key={idx}>
              <img
                src={img}
                alt={`${altText} - Slide ${idx + 1}`}
                className="slider-image"
                loading="lazy"
              />
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button className="slide-arrow prev-arrow" onClick={handlePrev} aria-label="Previous image">
          <svg viewBox="0 0 24 24"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
        </button>
        <button className="slide-arrow next-arrow" onClick={handleNext} aria-label="Next image">
          <svg viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
        </button>

        {/* Progress Indicators */}
        <div className="screen-indicators">
          {images.map((_, idx) => (
            <button
              key={idx}
              className={`indicator-dot ${idx === currentIndex ? "active" : ""}`}
              onClick={(e) => handleDotClick(idx, e)}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      </div>
      
      {/* Laptop Base Deck */}
      <div className="laptop-base">
        <div className="laptop-base-reflection" />
      </div>
    </div>
  );
};

const AutoBackupSoftware = () => {
  const navigate = useNavigate();

  const backupImages = [
    "/images/Autobackup-images/1.png",
    "/images/Autobackup-images/2.png",
    "/images/Autobackup-images/3.png",
    "/images/Autobackup-images/4.png",
    "/images/Autobackup-images/5.png",
  ];

  const features = [
    { 
      name: "Automated Scheduling", 
      icon: <FaCloudUploadAlt />, 
      description: "Program hourly, daily, weekly, or monthly backup cycles with complete hands-free execution.",
      details: "Configurable background scheduler triggers background backups quietly, ensuring your records are kept fresh without active maintenance."
    },
    { 
      name: "Dual-Redundancy Sync", 
      icon: <FaSyncAlt />, 
      description: "Secure data preservation split across local network drives and cloud storage endpoints.",
      details: "Protects against hardware failures and local disasters by keeping local NAS/server copies synchronized with protected cloud directories."
    },
    { 
      name: "High-Speed Restoration", 
      icon: <FaHistory />, 
      description: "Zero-data-loss restoration tools ensuring minimal downtime in emergency scenarios.",
      details: "Restore whole backup directory segments in a single click, getting your team back to operations with minimal business disruption."
    },
    { 
      name: "Smart Compression & Encryption", 
      icon: <FaShieldAlt />, 
      description: "Advanced AES-256 standard data protection with efficient folder storage optimization.",
      details: "Backups are zipped dynamically and protected with enterprise-level encryption, reducing bandwidth and server storage requirements."
    }
  ];

  return (
    <div className="autobackup-page-wrapper">
      {/* Back to Solutions Header */}
      <div className="back-nav-container">
        <button className="btn-back" onClick={() => navigate('/solutions')}>
          <FaArrowLeft /> Back to Solutions
        </button>
      </div>

      <div className="autobackup-hero-section">
        <div className="header-tagline-badge">
          <span className="live-dot" /> Data Protection & Continuity
        </div>
        <h1>Enterprise Auto Backup Software</h1>
        <p className="autobackup-intro-text">
          Protect your critical datasets, systems, and Tally ERP databases with automated background scheduling, smart encryption, and dual redundancy vaults.
        </p>
      </div>

      <div className="autobackup-content-grid">
        {/* Left Section: Showcase & Interactive Laptop Mockup */}
        <div className="autobackup-visual-showcase">
          <LaptopSlideshow images={backupImages} altText="Enterprise Auto Backup Software Screenshots" />
          <div className="visual-caption">
            <span>✨ Real-time dashboard and backup automation settings previews</span>
          </div>
        </div>

        {/* Right Section: Core Capabilities derived from Web Applications Page */}
        <div className="autobackup-features-list">
          <h3>Core Capabilities</h3>
          <p className="features-subtext">
            Engineered for high performance, reliability, and ease of use in corporate environments.
          </p>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card-v3">
                <div className="feature-card-header">
                  <span className="feature-card-icon">{feature.icon}</span>
                  <h4>{feature.name}</h4>
                </div>
                <p className="feature-card-desc">{feature.description}</p>
                <p className="feature-card-details">{feature.details}</p>
              </div>
            ))}
          </div>

          <div className="cta-box">
            <h4>Ready to secure your business intelligence?</h4>
            <p>Deploy our Enterprise Auto Backup utility tailored specifically for TallyPrime and multi-endpoint environments.</p>
            <button className="btn-cta" onClick={() => navigate('/contact')}>
              Request Live Demo <FaArrowRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoBackupSoftware;
